import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { fetchProductRatingInfo } from "@/api/productEvaluationApi";
import api from "@/utils/api";
import { API_ENDPOINTS, ApiResponse, API } from "@/constants/api";
import { fontFamily } from "@/styles/font";
import logger from "@/utils/logger";

interface AlcoholData {
    breweryInfo?: {
        breweryName: string;
    };
    name: string;
    type: string;
    thumbnailImageUrl: string | null;
}

interface ProductInfoProps {
    productAltKey: string;
    onMoveToReview: () => void;
    currentTab: string;
}

const ProductInfo: React.FC<ProductInfoProps> = ({ productAltKey, onMoveToReview, currentTab }) => {
    const defaultImagePath = "/images/default-image.png";
    const navigate = useNavigate();
    const [loading, setLoading] = useState<boolean>(true);
    const [alcoholData, setAlcoholData] = useState<AlcoholData | null>(null);
    const [ratingInfo, setRatingInfo] = useState<{ reviewCount: number; avgRating: number } | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            if (productAltKey) {
                try {
                    setLoading(true);
                    // 상품 정보 가져오기
                    const alcoholResponse = await api.get<ApiResponse>(`${API_ENDPOINTS.ALCOHOLS}/${productAltKey}`);

                    // 평점 정보 가져오기
                    const ratingData = await fetchProductRatingInfo(productAltKey);

                    if (alcoholResponse.data.success) {
                        setAlcoholData(alcoholResponse.data.data);
                    }

                    setRatingInfo(ratingData);
                } catch (error) {
                    logger.error("상품 정보 조회 실패:", error);
                } finally {
                    setLoading(false);
                }
            }
        };

        fetchData();
    }, [productAltKey]);

    const handleReviewButtonClick = () => {
        navigate(`/alcohol/${productAltKey}/review/new`);
    };

    // 평점 클릭 핸들러 수정
    const handleRatingClick = () => {
        onMoveToReview();
    };

    if (loading || !alcoholData) {
        return <LoadingWrapper>로딩 중...</LoadingWrapper>;
    }

    return (
        <ProductInfoWrapper>
            <ProductImage
                src={
                    alcoholData.thumbnailImageUrl
                        ? `${API.IMAGE_DOMAIN}/${alcoholData.thumbnailImageUrl}`
                        : defaultImagePath
                }
                alt={alcoholData.name}
                width={300}
                height={300}
                fetchPriority="high"
                loading="eager"
                decoding="async"
                sizes="(max-width: 430px) 100vw, 430px"
                onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = defaultImagePath;
                }}
            />
            <InfoSection>
                <CompanyName>{alcoholData.breweryInfo?.breweryName || ""}</CompanyName>
                <ProductName>{alcoholData.name}</ProductName>
                <CategoryWrapper>
                    <CategoryBadge>{alcoholData.type}</CategoryBadge>
                    {/* <RankInfo>21위</RankInfo> */}
                </CategoryWrapper>
                <RatingWrapper onClick={handleRatingClick} style={{ cursor: "pointer" }}>
                    <StarRating>
                        {[1, 2, 3, 4, 5].map((star) => (
                            <Star key={star} />
                        ))}
                    </StarRating>
                    <RatingScore>{ratingInfo ? ratingInfo.avgRating.toFixed(1) : "0.0"}</RatingScore>
                    <RatingCount>{ratingInfo ? `(${ratingInfo.reviewCount.toLocaleString()})` : "(0)"}</RatingCount>
                </RatingWrapper>
            </InfoSection>
            <ButtonGroup>
                <WriteReviewButton onClick={handleReviewButtonClick}>
                    <ButtonText>리뷰 작성하기</ButtonText>
                </WriteReviewButton>
            </ButtonGroup>
        </ProductInfoWrapper>
    );
};

export default ProductInfo;

const ProductInfoWrapper = styled.div`
    width: 100%;
`;

const ProductImage = styled.img`
    width: 100%;
    aspect-ratio: 1/1;
    max-height: 430px;
    border-radius: 28px;
    object-fit: contain;
    loading: eager;
    decoding: async;
`;

const InfoSection = styled.div`
    margin-top: 20px;
    display: flex;
    flex-direction: column;
    gap: 6px;
    width: 100%;
`;

const CompanyName = styled.div`
    display: flex;
    align-items: center;
    min-height: 10px;
    font-family: ${fontFamily.base};
    font-style: normal;
    font-weight: 350;
    font-size: 12px;
    line-height: 1.2;
    color: #000000;
    width: 100%;
    word-break: break-word;

    @media screen and (max-width: 768px) {
        font-size: 11px;
    }
`;

const ProductName = styled.h1`
    width: auto;
    min-height: 34px;
    display: flex;
    align-items: center;
    font-family: ${fontFamily.base};
    font-style: normal;
    font-weight: 700;
    font-size: 28px;
    line-height: 1.2;
    color: #000000;
    word-break: break-word;

    @media screen and (max-width: 768px) {
        font-size: 24px;
    }

    @media screen and (max-width: 480px) {
        font-size: 22px;
    }
`;

const CategoryWrapper = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 10px;
    margin-top: 7px;
    flex-wrap: wrap;
`;

const CategoryBadge = styled.div`
    box-sizing: border-box;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 1px 8px;
    min-width: 67px;
    height: 20px;
    border: 1px solid #ffc700;
    border-radius: 10px;
    font-family: ${fontFamily.base};
    font-style: normal;
    font-weight: 700;
    font-size: 14px;
    line-height: 17px;
    color: #ffc700;
    white-space: nowrap;

    @media screen and (max-width: 480px) {
        font-size: 12px;
        min-width: 60px;
        height: 18px;
    }
`;

const RankInfo = styled.span`
    min-width: 25px;
    height: auto;
    font-family: ${fontFamily.base};
    font-style: normal;
    font-weight: 350;
    font-size: 12px;
    line-height: 1.2;
    display: flex;
    align-items: center;
    color: #000000;
    word-break: break-word;
`;

const RatingWrapper = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 0px;
    gap: 5px;
    margin-top: 10px;
    flex-wrap: wrap;
`;

const StarRating = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 0px;
    gap: 2px;
    max-width: 108px;
    height: 20px;

    @media screen and (max-width: 480px) {
        max-width: 100px;
        height: 18px;
    }
`;

const Star = styled.div`
    width: 20px;
    height: 20px;
    background: url("/icon/star.png") no-repeat center;
    background-size: contain;
    flex: none;
    order: 0;
    flex-grow: 0;

    @media screen and (max-width: 480px) {
        width: 18px;
        height: 18px;
    }
`;

const RatingScore = styled.span`
    min-width: 20px;
    height: auto;
    display: flex;
    align-items: center;
    font-family: ${fontFamily.base};
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 1.2;

    @media screen and (max-width: 480px) {
        font-size: 13px;
    }
`;

const RatingCount = styled.span`
    min-width: 38px;
    height: auto;
    display: flex;
    align-items: center;
    font-family: ${fontFamily.base};
    font-style: normal;
    font-weight: 350;
    font-size: 12px;
    line-height: 1.2;

    @media screen and (max-width: 480px) {
        font-size: 11px;
    }
`;

const ButtonGroup = styled.div`
    position: relative;
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
    margin-top: 24px;
`;

const WriteReviewButton = styled.button`
    /* Auto layout */
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    padding: 12px 16px;
    gap: 8px;

    height: 48px;

    /* 브랜드/메인 컬러 */
    background: #007f73;
    border-radius: 12px;
    border: none;
    cursor: pointer;

    /* Inside auto layout */
    flex: 1;

    @media screen and (max-width: 480px) {
        height: 44px;
        padding: 10px 14px;
    }

    @media screen and (max-width: 320px) {
        height: 40px;
        padding: 8px 12px;
    }
`;

const ButtonText = styled.span`
    font-family: ${fontFamily.base};
    font-style: normal;
    font-weight: 500;
    font-size: 16px;
    line-height: 24px;
    color: #ffffff;

    @media screen and (max-width: 480px) {
        font-size: 15px;
        line-height: 22px;
    }

    @media screen and (max-width: 320px) {
        font-size: 14px;
        line-height: 20px;
    }
`;

const BookmarkButton = styled.button`
    /* Icon-Button */
    box-sizing: border-box;

    /* Auto layout */
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 0px;

    width: 56px;
    height: 48px;

    background: #ffffff;
    border: 1px solid #e8eef2;
    border-radius: 12px;
    cursor: pointer;

    /* Inside auto layout */
    flex: none;

    @media screen and (max-width: 480px) {
        width: 52px;
        height: 44px;
    }

    @media screen and (max-width: 320px) {
        width: 48px;
        height: 40px;
    }
`;

const BookmarkIcon = styled.img`
    width: 24px;
    height: 24px;

    @media screen and (max-width: 320px) {
        width: 20px;
        height: 20px;
    }
`;

const LoadingWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 200px;
    width: 100%;
    font-family: ${fontFamily.base};
    font-size: 16px;
    color: #767676;
`;
